import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User, X } from 'lucide-react';
import { useStep } from '@/hooks/useStep';
import { useDebounce } from '@/hooks/useDebounce';

import { useGetUsers, useGetClubs } from '@/services';
import { UserColumnsSession } from './UserColumnsSession';
import {
	advanceSessionWizardStep,
	decodeUrl,
	encodeUrl,
	getSessionWizardListPanelKey,
} from '@/utils';
import { buildPatientFilters } from '@/utils/patientFilters';
import {
	DataTable,
	SessionStepTitle,
	StepButtonContainer,
	Filter,
	SearchInput,
} from '@/components';
import {
	Avatar,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from '@/components/ui';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const SESSION_TYPE_OPTIONS = [
	{ label: 'Individual', value: 'false' },
	{ label: 'Em grupo', value: 'true' },
] as const;

const StepPacient = () => {
	const professionalsContainerRef = useRef<HTMLDivElement>(null);
	const { nextStep, currentStep } = useStep();
	const [searchParams, setSearchParams] = useSearchParams();
	const [patientSearch, setPatientSearch] = useState('');
	const debouncedPatientSearch = useDebounce(patientSearch, 300);
	const listPanelKey = getSessionWizardListPanelKey(searchParams, 'patient');
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(10);
	type SelectedDetail = {
		id: string;
		first_name: string;
		last_name: string;
		profile_photo_url?: string | null;
	};
	const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
	const [selectedUserDetails, setSelectedUserDetails] = useState<
		Map<string, SelectedDetail>
	>(new Map());
	const [isGroupSession, setIsGroupSession] = useState(false);
	const [maxParticipantCount, setMaxParticipantCount] = useState(10);

	const navigate = useNavigate();

	const updateUrl = (updates: Record<string, string | number | boolean>) => {
		const encodedUrl = searchParams.get('d');
		const decodedUrl = encodedUrl
			? (decodeUrl(encodedUrl) as Record<
					string,
					string | number | boolean
			  >)
			: {};
		const newEncodedUrl = encodeUrl({
			...decodedUrl,
			...updates,
			s: '1',
		});
		searchParams.set('d', newEncodedUrl);
		navigate(`?${searchParams}`, { replace: true });
	};

	const { data: clubsData } = useGetClubs();
	const clubs = Array.isArray(clubsData) ? clubsData : clubsData?.data ?? [];
	const filters = buildPatientFilters(clubs);

	const bannedParams = searchParams.get('banned') as 'true' | 'false';
	const isCompleteParams = searchParams.get('is_complete') as
		| 'true'
		| 'false';
	const clubIdParams = searchParams.get('club_id') || undefined;
	const encodedUrl = searchParams.get('d');

	const {
		data: usersData,
		isFetching,
		isError,
	} = useGetUsers({
		page,
		limit,
		...(bannedParams && { banned: bannedParams }),
		...(isCompleteParams && { is_complete: isCompleteParams }),
		...(clubIdParams && { club_id: clubIdParams }),
		...(debouncedPatientSearch && { search: debouncedPatientSearch }),
	});

	useEffect(() => {
		if (encodedUrl) {
			const decodedUrl = decodeUrl(encodedUrl) as Record<string, unknown>;

			let initialIds: string[] = [];
			if (typeof decodedUrl?.user_ids === 'string') {
				try {
					const parsed = JSON.parse(decodedUrl.user_ids) as string[];
					if (Array.isArray(parsed))
						initialIds = parsed.filter(Boolean);
				} catch {
					initialIds = [];
				}
			}
			if (initialIds.length > 0) {
				setSelectedUserIds(initialIds);
				setSelectedUserDetails(new Map());
			}

			if (decodedUrl.is_group === 'true') setIsGroupSession(true);
			else if (decodedUrl.is_group === 'false') setIsGroupSession(false);
			const mp = decodedUrl.max_participants;
			const mpNum =
				typeof mp === 'number'
					? mp
					: typeof mp === 'string'
					  ? parseInt(mp, 10)
					  : NaN;
			if (!isNaN(mpNum) && mpNum >= 2) setMaxParticipantCount(mpNum);

			const newEncodedUrl = encodeUrl({
				...decodedUrl,
				s: '1',
			});

			searchParams.set('d', newEncodedUrl);
			navigate(`?${searchParams}`, { replace: true });
			return;
		}
		const newEncodedUrl = encodeUrl({
			s: '1',
			user_ids: '[]',
			is_group: false,
		});
		searchParams.set('d', newEncodedUrl);
		navigate(`?${searchParams}`, { replace: true });
	}, []);

	useEffect(() => {
		if (page !== 1) handlePageChange(1);
	}, [bannedParams, isCompleteParams, clubIdParams, debouncedPatientSearch]);

	// Auto-selecionar "Em grupo" quando mais de 1 paciente selecionado
	useEffect(() => {
		if (selectedUserIds.length > 1) {
			setIsGroupSession(true);
		}
	}, [selectedUserIds.length]);

	// Garantir que o máximo de participantes nunca seja menor
	// que o total de pacientes selecionados (quando em grupo)
	useEffect(() => {
		if (!isGroupSession) return;
		if (selectedUserIds.length === 0) return;

		const minValue = Math.max(2, selectedUserIds.length);
		if (maxParticipantCount < minValue) {
			setMaxParticipantCount(minValue);
			updateUrl({ max_participants: minValue });
		}
	}, [isGroupSession, selectedUserIds.length, maxParticipantCount]);

	// Preencher detalhes dos selecionados a partir da página atual (ex.: ao voltar com vários IDs na URL)
	useEffect(() => {
		if (selectedUserIds.length > 0 && usersData?.data?.length) {
			const toAdd = usersData.data
				.filter((u) => selectedUserIds.includes(u.id))
				.filter((u) => !selectedUserDetails.has(u.id))
				.map((u) => ({
					id: u.id,
					first_name: u.first_name ?? '',
					last_name: u.last_name ?? '',
					profile_photo_url: u.profile_photo_url ?? undefined,
				}));
			if (toAdd.length > 0) {
				setSelectedUserDetails((prev) => {
					const next = new Map(prev);
					toAdd.forEach((n) => next.set(n.id, n));
					return next;
				});
			}
		}
	}, [selectedUserIds, selectedUserDetails, usersData?.data]);

	const handleUserSelected = (user: Users.User) => {
		const exists = selectedUserIds.includes(user.id);
		const nextIds = exists
			? selectedUserIds.filter((id) => id !== user.id)
			: [...selectedUserIds, user.id];

		if (exists) {
			setSelectedUserDetails((prev) => {
				const next = new Map(prev);
				next.delete(user.id);
				return next;
			});
		} else {
			setSelectedUserDetails((prev) =>
				new Map(prev).set(user.id, {
					id: user.id,
					first_name: user.first_name ?? '',
					last_name: user.last_name ?? '',
					profile_photo_url: user.profile_photo_url ?? undefined,
				}),
			);
		}
		setSelectedUserIds(nextIds);

		updateUrl({
			user_ids: JSON.stringify(nextIds),
			is_group: nextIds.length > 1 ? true : isGroupSession,
			...(isGroupSession &&
				nextIds.length === 0 && {
					max_participants: maxParticipantCount,
				}),
		});
	};

	const handleRemoveParticipant = (id: string) => {
		const user = usersData?.data?.find((u) => u.id === id);
		if (user) handleUserSelected(user);
		else {
			const nextIds = selectedUserIds.filter((x) => x !== id);
			setSelectedUserIds(nextIds);
			setSelectedUserDetails((prev) => {
				const next = new Map(prev);
				next.delete(id);
				return next;
			});
			updateUrl({
				user_ids: JSON.stringify(nextIds),
				is_group: nextIds.length > 1 ? true : isGroupSession,
				...(isGroupSession &&
					nextIds.length === 0 && {
						max_participants: maxParticipantCount,
					}),
			});
		}
	};

	const selectedCardsList = useMemo(() => {
		const fromTable = usersData?.data ?? [];
		const byId = new Map(
			fromTable.map((u) => [
				u.id,
				{
					first_name: u.first_name ?? '',
					last_name: u.last_name ?? '',
					profile_photo_url: u.profile_photo_url,
				},
			]),
		);
		selectedUserDetails.forEach((v, id) =>
			byId.set(id, {
				first_name: v.first_name,
				last_name: v.last_name,
				profile_photo_url: v.profile_photo_url,
			}),
		);
		return selectedUserIds.map((id) => {
			const d = byId.get(id) ?? selectedUserDetails.get(id);
			const first = d?.first_name ?? '';
			const last = d?.last_name ?? '';
			return {
				id,
				first_name: first,
				last_name: last,
				fullName: `${first} ${last}`.trim() || '—',
				profile_photo_url: d?.profile_photo_url,
			};
		});
	}, [selectedUserIds, selectedUserDetails, usersData?.data]);

	const handleSessionTypeChange = (val: boolean) => {
		setIsGroupSession(val);
		updateUrl({
			is_group: val,
			...(val &&
				selectedUserIds.length === 0 && {
					max_participants: maxParticipantCount,
				}),
		});
	};

	const handleNextStep = () => {
		setPatientSearch('');
		const nextParams = advanceSessionWizardStep(searchParams, {
			s: currentStep + 1,
			user_ids: JSON.stringify(selectedUserIds),
			is_group: isGroupSession,
			...(isGroupSession &&
				selectedUserIds.length === 0 && {
					max_participants: maxParticipantCount,
				}),
		});
		setSearchParams(nextParams, { replace: true });
		nextStep();
	};
	const handlePrevius = () => navigate('/admin/consultas');
	const handlePageChange = (newPage: number) => setPage(newPage);

	const handleLimitChange = (newLimit: number) => {
		setLimit(newLimit);
		setPage(1);
	};

	const handleDeselectAll = () => {
		setSelectedUserIds([]);
		setSelectedUserDetails(new Map());
		updateUrl({
			user_ids: JSON.stringify([]),
			is_group: isGroupSession,
			...(isGroupSession && { max_participants: maxParticipantCount }),
		});
	};

	return (
		<section className="flex w-full flex-col items-center gap-6">
			{/* HEADER: Título e Filtros */}
			<div className="flex w-full flex-col gap-4">
				<SessionStepTitle
					className="w-fit"
					title="Selecione um paciente:"
					icon={<User className="w-5" />}
				/>

				<div className="w-full min-w-0">
					<SearchInput
						key={`session-wizard-patient-search-${listPanelKey}`}
						syncToUrl={false}
						value={patientSearch}
						onValueChange={setPatientSearch}
						placeholder="Pesquisar por nome..."
						className="w-full"
					/>
				</div>

				<div className="w-full min-w-0">
					<Filter
						key={`session-wizard-patient-filters-${listPanelKey}`}
						filters={filters}
					/>
				</div>

				<div className="flex flex-wrap items-end gap-6">
					<div className="flex flex-col">
						<label className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-500">
							Tipo de sessão
						</label>
						<div
							className={cn(
								selectedUserIds.length > 1 &&
									'pointer-events-none opacity-60',
							)}
						>
							<Select
								value={String(isGroupSession)}
								onValueChange={(v) =>
									handleSessionTypeChange(v === 'true')
								}
							>
								<SelectTrigger
									className="h-11 w-full min-w-[160px] rounded-xl border-stone-200 bg-white font-semibold text-zinc-600 shadow-sm"
									hideTriggerIcon={true}
								>
									<span
										className={cn(
											'flex-1 text-left',
											selectedUserIds.length > 1 &&
												'text-zinc-400',
										)}
									>
										{SESSION_TYPE_OPTIONS.find(
											(o) =>
												o.value ===
												String(isGroupSession),
										)?.label ??
											(isGroupSession
												? 'Em grupo'
												: 'Individual')}
									</span>
									<ChevronDown className="h-4 w-4 opacity-50" />
								</SelectTrigger>
								<SelectContent className="bg-white">
									{SESSION_TYPE_OPTIONS.map((opt) => (
										<SelectItem
											key={opt.value}
											value={opt.value}
											indicator={false}
										>
											{opt.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
					<div className="flex flex-col">
						<label className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-500">
							Máx. participantes
						</label>
						<input
							type="number"
							min={
								isGroupSession
									? Math.max(2, selectedUserIds.length || 0)
									: 1
							}
							max={100}
							value={isGroupSession ? maxParticipantCount : 1}
							readOnly={!isGroupSession}
							onChange={(e) => {
								if (!isGroupSession) return;
								const raw = parseInt(e.target.value, 10);
								if (isNaN(raw)) return;

								const minValue = Math.max(
									2,
									selectedUserIds.length || 0,
								);
								const clamped = Math.max(
									minValue,
									Math.min(100, raw),
								);

								setMaxParticipantCount(clamped);
								updateUrl({ max_participants: clamped });
							}}
							className={cn(
								'h-11 w-20 rounded-xl border border-stone-200 px-3 py-1.5 text-sm font-bold text-stone-800',
								!isGroupSession &&
									'cursor-default bg-stone-50 text-stone-500',
							)}
						/>
					</div>
				</div>
			</div>

			{/* TABELA */}
			{usersData && (
				<div
					ref={professionalsContainerRef}
					className="flex w-full flex-col gap-4"
				>
					{usersData.data.length > 0 ? (
						<>
							<DataTable
								columns={UserColumnsSession({
									onSelect: handleUserSelected,
									selectedUserIds,
									clubs,
									variant: 'selectPatient',
								})}
								data={usersData.data}
								selectedItems={selectedUserIds}
								page={page}
								limit={limit}
								totalItems={usersData.total}
								onPageChange={handlePageChange}
								onLimitChange={handleLimitChange}
							/>

							{/* FEEDBACK DE SELEÇÃO (igual a Ver participantes) */}
							{selectedUserIds.length > 0 && (
								<div className="animate-in fade-in slide-in-from-left-2 flex w-full flex-col overflow-hidden rounded-2xl border border-solid border-stone-200 bg-white shadow-sm duration-300">
									<div className="flex items-center justify-between border-b border-stone-200 bg-stone-50/50 px-6 py-4">
										<span className="text-[10px] font-black uppercase tracking-widest text-stone-400">
											Total selecionado:{' '}
											{selectedUserIds.length}
										</span>
										<button
											type="button"
											onClick={handleDeselectAll}
											className="rounded-lg p-1.5 text-stone-400 transition-colors hover:bg-stone-200/60 hover:text-stone-700"
											aria-label="Desmarcar todos"
										>
											<X size={18} strokeWidth={2.5} />
										</button>
									</div>
									<div className="flex flex-wrap gap-2 p-4">
										{selectedCardsList.map((card, idx) => {
											const paletteBg = [
												'bg-zinc-100',
												'bg-stone-100',
												'bg-zinc-50',
											][idx % 3];
											return (
												<div
													key={card.id}
													className={cn(
														'flex items-center gap-2 rounded-xl border border-stone-200/80 px-3 py-2 shadow-sm',
														paletteBg,
													)}
												>
													{card.profile_photo_url ? (
														<Avatar
															className="h-7 w-7 shrink-0 rounded-full"
															variant="circle"
															img_url={
																card.profile_photo_url
															}
														/>
													) : (
														<div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-stone-200/80 text-[10px] font-bold uppercase text-stone-600">
															{[
																card.first_name.trim()[0],
																card.last_name.trim()[0],
															]
																.filter(Boolean)
																.join('') ||
																'?'}
														</div>
													)}
													<span className="max-w-[120px] truncate text-xs font-bold text-stone-800">
														{card.fullName}
													</span>
													<button
														type="button"
														onClick={() =>
															handleRemoveParticipant(
																card.id,
															)
														}
														className="-mr-0.5 rounded-lg p-1 text-stone-400 transition-colors hover:bg-stone-200/60 hover:text-stone-700"
														aria-label="Remover participante"
													>
														<X
															size={14}
															strokeWidth={2.5}
														/>
													</button>
												</div>
											);
										})}
									</div>
								</div>
							)}
						</>
					) : !isFetching && !isError ? (
						<div className="flex w-full flex-col items-center justify-center rounded-3xl border-2 border-dashed border-stone-200 bg-stone-50/50 py-20">
							<User className="mb-3 size-12 text-stone-300" />
							<p className="text-[11px] font-black uppercase tracking-widest text-stone-500">
								Nenhum paciente encontrado
							</p>
						</div>
					) : null}
				</div>
			)}

			{/* FOOTER */}
			<div className="w-full border-t border-stone-100 pt-4">
				<StepButtonContainer
					disabledNextButton={
						isGroupSession ? false : selectedUserIds.length !== 1
					}
					onHandleNext={handleNextStep}
					onHandlePrevius={handlePrevius}
					isFirstStep={true}
				/>
			</div>
		</section>
	);
};
export { StepPacient };
